import {useSelector} from "react-redux";
import {Store} from "../../../store/reducer";
import {useTranslation} from "react-i18next";
import React, {useEffect, useMemo, useState} from "react";
import {LabelLoginRequired} from "../../shared/LabelLoginRequired";
import './GeradorRotulos.css';
import {useStickyState} from "../../shared/UseStickyState";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import {AuthService} from "../../../services/AuthService";

function parseWeight(t: any): any {
  if (!t) return undefined;

  let result = /([0-9]+) ?(\w+)/.exec(t as string)
  if (!result) return undefined;
  let val = parseInt(result[1]);
  let unit = result[2]

  switch (unit) {
    case 'g':
      return val;
    case 'mg':
      return val / 1000;
    case 'ng':
      return val / 1000000;
    case 'kg':
      return val * 1000;
    default:
      return undefined;
  }
}

function parseKcal(t: any): any {
  if (!t) return undefined;

  let result = /([0-9]+) ?(\w+)/.exec(t)
  if (!result) return undefined;
  let val = result[1]
  let unit = result[2]

  if (unit !== "kcal") return undefined;

  return parseFloat(val)
}

function weightToStr(w: any): any {
  if (isNaN(w)) return '';
  w /= 1000;
  if (w >= 1) return +w.toFixed(2) + " kg"
  w *= 1000;
  if (w >= 1) return +w.toFixed(2) + " g"
  w *= 1000;
  if (w >= 1) return +w.toFixed(2) + " mg"
  w *= 1000;
  return +w.toFixed(2) + " ng"
}


export function GeradorRotulos() {
  const {t} = useTranslation();

  const {loggedIn, user} = useSelector((state: Store) => state.AuthState);
  const hasPermission = useMemo(() => {
    return loggedIn && AuthService.hasPermission(user, "ketnutri");
  }, [loggedIn, user]);

  const [portion, setPortion] = useStickyState('Porção de ---', 'rotulo.portion');
  const [portionDesc, setPortionDesc] = useStickyState('', 'rotulo.portionDesc');
  const [values, setValues] = useStickyState('', 'rotulo.values');
  const [scale, setScale] = useStickyState('', 'rotulo.scale');

  const [portionError, setPortionError] = useState(false);
  const [scaleError, setScaleError] = useState(false);

  const [portionTitle, setPortionTitle] = useState("");
  const [valuesArray, setValuesArray] = useState([] as any[]);

  useEffect(() => {
    let portionValue = parseWeight(portion);
    setPortionError(isNaN(portionValue));

    let scaleValue = parseWeight(scale);
    setScaleError(isNaN(scaleValue) || isNaN(portionValue));

    setPortionTitle(t(portionDesc ? "rotulo.portion_title_desc" : "rotulo.portion_title", {
      value: weightToStr(scaleValue || portionValue),
      desc: portionDesc
    }))

    setValuesArray(values
      .split('\n')
      .map(v => v.trim())
      .filter(v => v)
      .map(v => {
        let reg = /^(- *)?(?:(.+?) ([0-9,.]+.+) ([0-9,.]+%|\*\*)|(?:(.+?)(?: +(.+?)(?: +(.+?))?)?))$/
        let result = reg.exec(v)
        if (result == null) return {validRow: false};

        let padLeft = !!result[1];

        let name: string
        let value: number | string
        let vd: number | string
        let validRow = false
        let isKcal = false

        if (result[2]) {
          validRow = true
          name = result[2]
          value = parseWeight(result[3].replace(',', '.'))

          if (typeof value !== 'number' || isNaN(value)) {
            value = parseKcal(result[3])
            if (value === undefined) {
              value = parseInt(result[3])
              validRow = false
            } else {
              isKcal = true
            }
          }

          vd = result[4]
        } else {
          name = result[5]
          value = parseInt(result[6])
          vd = result[7]
        }

        let vdNaoEstabelecido = vd === "**"

        if (validRow) {
          if (!scaleError) {
            value = (value as number) / portionValue * scaleValue
            if (!vdNaoEstabelecido) {
              // vd is number
              vd = (/([0-9]+)%/.exec(vd) || [null, 0])[1] || 0
              vd = (vd as number) / portionValue * scaleValue
              vd = +vd.toFixed(2) + "%"
            }
          }

          if (isKcal)
            value = +(value as number).toFixed(2) + " kcal = " + +((value as number) * 4.184).toFixed(2) + " kJ"
          else
            value = weightToStr(value)
        }


        return ({
          name: name,
          value: value,
          vd: vd,
          validRow: validRow,
          isKcal: isKcal,
          padLeft: padLeft,
          vdNaoEstabelecido: vdNaoEstabelecido
        });
      }));
  }, [portion, portionDesc, values, scale, portionError, scaleError, t])

  if (!hasPermission) return <LabelLoginRequired loggedIn={loggedIn}/>;

  function mapValueLine(valueLine: any) {
    const {name, value, vd, validRow, padLeft} = valueLine;
    return (
      <tr key={valueLine} className={validRow ? '' : 'bg-invalid-row'}>
        <td className={padLeft ? 'pl-3' : ''}>{name}</td>
        <td>{value}</td>
        <td>{vd}</td>
      </tr>
    )
  }

  return (
    <div className="container mt-3">
      <div className="row">
        <div className="col-lg-6 col-md-12 rotulo-container p-3">
          <table className=" mx-auto">
            <thead>
            <tr/>
            <tr/>
            <tr/>
            </thead>
            <tbody>
            <tr>
              <td colSpan={3}>{t("rotulo.title")}</td>
            </tr>
            <tr>
              <td colSpan={3} id="titulo-portion">{portionTitle}</td>
            </tr>
            <tr>
              <td colSpan={2}>{t("rotulo.quantity_per_portion")}</td>
              <td>{t("rotulo.vd_perc")} (*)</td>
            </tr>
            {valuesArray.map(mapValueLine)}
            <tr id="after-dynamic">
              <td colSpan={3}>
                * {t("rotulo.vd_info_1")}<br/>
                {t("rotulo.vd_info_2")}<br/>
                {t("rotulo.vd_info_3")}<br/>
                {t("rotulo.vd_info_4")}
              </td>
            </tr>
            {valuesArray.filter(v => v.vdNaoEstabelecido).length === 0 || (
              <tr id="vd-nao-estabelecido"><td colSpan={3}>** {t("rotulo.no_vd")}</td></tr>
            )}
            </tbody>
          </table>
        </div>
        <div className="col-md-6">
          <form id="form">
            <h4>Dados</h4>
            <div className="row">
              <div className="col-6">
                <div className="form-group">
                  <label htmlFor="portion">{t("rotulo.portion")}{!portionError || (
                    <FontAwesomeIcon icon={faExclamationTriangle} color="#f7cd29" className="ml-2"/>)}</label>
                  <input onChange={e => setPortion(e.target.value)} type="text" className="form-control shrink-for-icon" id="portion" placeholder={t("rotulo.portion_placeholder")}/>
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label htmlFor="portion-extra">{t("rotulo.portion_desc")}</label>
                  <input onChange={e => setPortionDesc(e.target.value)} type="text" className="form-control" id="portion-extra" placeholder={t("rotulo.portion_desc_placeholder")}/>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label htmlFor="values">{t("rotulo.values")}</label>
                  <textarea onChange={e => setValues(e.target.value)} className="form-control" id="values" placeholder={t("rotulo.values_placeholder")} rows={8}/>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="scale">{t("rotulo.scale")}{!scaleError || (
                    <FontAwesomeIcon icon={faExclamationTriangle} color="#f7cd29" className="ml-2"/>)}</label>
                  <input onChange={e => setScale(e.target.value)} type="text" className="form-control" id="scale" placeholder={t("rotulo.scale_placeholder")}/>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
